import { Component } from '@theme/component';

/**
 * @typedef {Object} ContactFormRefs
 * @property {HTMLInputElement} emailInput - The contact email field
 * @property {HTMLInputElement} emailMarketingInput - The "receive marketing emails" checkbox
 * @property {HTMLSelectElement} countrySelect - The phone country/dial-code select
 * @property {HTMLInputElement} phoneInput - The phone number field
 */

/**
 * Handles the contact form: subscribes the email to marketing in the
 * background (Shopify's native customer newsletter signup) when the
 * consent checkbox is checked, without interrupting the normal contact
 * form submission. Also keeps the phone field's dial code in sync with
 * the selected country.
 *
 * @extends {Component<ContactFormRefs>}
 */
class ContactFormComponent extends Component {
  requiredRefs = ['emailInput', 'emailMarketingInput', 'countrySelect', 'phoneInput'];

  /** @type {string} */
  #previousDialCode = '';

  connectedCallback() {
    super.connectedCallback();
    this.#previousDialCode = this.refs.countrySelect.value;
  }

  /**
   * Subscribes the entered email to marketing (fire-and-forget) before the
   * normal contact form submission navigates the page away.
   */
  handleSubmit = () => {
    const { emailInput, emailMarketingInput } = this.refs;

    if (!emailMarketingInput.checked || !emailInput.value) return;

    const formData = new FormData();
    formData.append('form_type', 'customer');
    formData.append('utf8', '✓');
    formData.append('contact[email]', emailInput.value);
    formData.append('contact[tags]', 'newsletter');

    navigator.sendBeacon(`${Theme.routes.root_url}contact`, formData);
  };

  /**
   * Updates the phone field's dial code prefix when the country changes,
   * unless the customer has already typed a number beyond the prefix.
   */
  handleCountryChange = () => {
    const { countrySelect, phoneInput } = this.refs;
    const newDialCode = countrySelect.value;

    if (phoneInput.value === '' || phoneInput.value === this.#previousDialCode) {
      phoneInput.value = newDialCode;
    }

    this.#previousDialCode = newDialCode;
  };
}

if (!customElements.get('contact-form-component')) {
  customElements.define('contact-form-component', ContactFormComponent);
}
